import { supabase } from '@/integrations/supabase/client';
import { paymentAPI } from '@/api/payment';
import { 
  PricingPlan, 
  Subscription, 
  Invoice, 
  PaymentMethod, 
  BillingPortalSession, 
  CheckoutSession,
  PaymentError,
  BillingInterval,
  PaymentMethodType
} from '@/types/payment';

// Pricing plans configuration
export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Perfect for getting started',
    price: { monthly: 0, annual: 0 },
    features: [
      'Up to 3 projects',
      '1GB file storage',
      'AI assistants and generation available with Pro or Teams',
      'Core file management',
      'Community support',
    ],
    cta: 'Get Started',
    limitations: [
      'Basic collaboration tools',
      'Community support only'
    ],
    popular: false,
    color: 'gray',
    icon: 'Star',
    maxProjects: 3,
    maxStorage: 1,
    maxUsers: 1,
    maxAIQueries: 0,
    includesAdvancedAI: false,
    includesCustomTraining: false,
    includesPrioritySupport: false,
    includesSSO: false,
    includesEnterpriseFeatures: false
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For active consultants and professionals',
    price: { monthly: 25, annual: 20 },
    features: [
      'Unlimited projects',
      '5GB file storage',
      'Unlimited AI prompts',
      'Complete Research Hub workflow',
      'Google services integration',
      'Advanced file organization',
      'Priority email support',
    ],
    cta: 'Get Started',
    popular: true,
    color: 'blue',
    icon: 'Zap',
    maxProjects: 50,
    maxStorage: 5,
    maxUsers: 1,
    maxAIQueries: -1, // -1 indicates unlimited
    includesAdvancedAI: true,
    includesCustomTraining: true,
    includesPrioritySupport: true,
    includesSSO: false,
    includesEnterpriseFeatures: false
  },
  {
    id: 'teams',
    name: 'Teams',
    description: 'For consulting teams and small agencies',
    price: { monthly: 49, annual: 39 },
    features: [
      'Everything in Pro',
      '100GB shared storage',
      'Unlimited AI prompts',
      'Voice agents',
      'Zoom call transcriptions',
      'Team workspace management',
      'Advanced collaboration tools',
      'SSO integration',
      'Priority support',
    ],
    cta: 'Get Started',
    popular: false,
    color: 'green',
    icon: 'Users',
    maxProjects: 100,
    maxStorage: 100,
    maxUsers: 10,
    maxAIQueries: -1, // -1 indicates unlimited
    includesAdvancedAI: true,
    includesCustomTraining: true,
    includesPrioritySupport: true,
    includesSSO: true,
    includesEnterpriseFeatures: false
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large consulting firms and enterprises',
    price: { monthly: 'Custom', annual: 'Custom' }, // Custom pricing
    features: [
      'Everything in Teams',
      'Unlimited storage',
      'Unlimited AI prompts',
      'Voice agents',
      'Zoom call transcriptions',
      'Custom AI workflows',
      'Enterprise-grade security',
      'Custom integrations',
      'Dedicated infrastructure',
      '24/7 phone support',
      'Training and onboarding',
    ],
    cta: 'Contact Sales',
    popular: false,
    color: 'purple',
    icon: 'Building',
    maxProjects: 1000,
    maxStorage: 1000,
    maxUsers: 100,
    maxAIQueries: -1, // -1 indicates unlimited
    includesAdvancedAI: true,
    includesCustomTraining: true,
    includesPrioritySupport: true,
    includesSSO: true,
    includesEnterpriseFeatures: true
  }
];

class PaymentService {
  private baseUrl = '/api/payments';

  // Get all pricing plans
  async getPricingPlans(): Promise<PricingPlan[]> {
    const { prices } = await paymentAPI.getPrices();
    return PRICING_PLANS.map(plan => {
      if (!['pro', 'teams'].includes(plan.id)) return plan;
      const monthly = prices.find(price => price.planId === plan.id && price.billingInterval === 'monthly');
      const annual = prices.find(price => price.planId === plan.id && price.billingInterval === 'annual');
      if (!monthly || !annual || monthly.currency !== annual.currency) throw new Error('Billing prices are unavailable.');
      return { ...plan, currency: monthly.currency, price: { monthly: monthly.amount, annual: annual.amount / 12 } };
    });
  }

  // Get current user's subscription
  async getCurrentSubscription(): Promise<Subscription | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .not('stripe_subscription_id', 'is', null)
        .not('stripe_customer_id', 'is', null)
        .in('status', ['active', 'trialing', 'past_due', 'unpaid', 'incomplete', 'paused'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }
  }

  // Create checkout session for subscription
  async createCheckoutSession(
    planId: string, 
    billingInterval: BillingInterval,
    successUrl?: string,
    cancelUrl?: string
  ): Promise<CheckoutSession> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Supabase creates the Checkout Session and the browser redirects to Stripe-hosted Checkout.
      const response = await paymentAPI.createCheckoutSession({
        planId,
        billingInterval
      });

      return response;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  // Create billing portal session
  async createBillingPortalSession(returnUrl?: string): Promise<BillingPortalSession> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Customer ownership and return URLs are resolved by the backend.
      const response = await paymentAPI.createPortalSession();

      return response;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }

  // Get user's invoices
  async getInvoices(): Promise<Invoice[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Stripe invoice amounts are stored as minor units in Supabase.
      return (data || []).map(invoice => ({ ...invoice, amount: invoice.amount / 100 }));
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return [];
    }
  }

  // Get user's payment methods
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      return [];
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const session = await this.createBillingPortalSession();
      window.location.assign(session.url);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  // Reactivate subscription
  async reactivateSubscription(subscriptionId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const session = await this.createBillingPortalSession();
      window.location.assign(session.url);
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  }

  // Update subscription
  async updateSubscription(
    subscriptionId: string, 
    planId: string, 
    billingInterval: BillingInterval
  ): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const session = await this.createBillingPortalSession();
      window.location.assign(session.url);
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  // Get usage metrics
  async getUsageMetrics(): Promise<any> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Get actual project count
      const actualProjectCount = await this.getActualProjectCount(user.id);
      
      // Use the new usage tracking service for other metrics
      const { usageTrackingService } = await import('@/services/usageTrackingService');
      const usageMetrics = await usageTrackingService.getUserUsage();
      
      // Transform the data to match the expected format
      const metrics = {
        projects_count: actualProjectCount,
        storage_used: 0,
        ai_queries_used: 0,
        ai_queries_limit: 0,
        users_count: 0,
        users_limit: 0
      };

      usageMetrics.forEach(metric => {
        switch (metric.metric_type) {
          case 'storage_used':
            metrics.storage_used = metric.current_value;
            break;
          case 'ai_queries':
            metrics.ai_queries_used = metric.current_value;
            metrics.ai_queries_limit = metric.limit_value;
            break;
          // Removed users_count to streamline the dashboard
        }
      });

      return metrics;
    } catch (error) {
      console.error('Error fetching usage metrics:', error);
      return null;
    }
  }

  // Get actual project count for user (using ProjectsContext logic)
  private async getActualProjectCount(userId: string): Promise<number> {
    try {
      // Get user profile to check role
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('role, email')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      let allUserProjects: any[] = [];

      // Use the same logic as ProjectsContext - get all projects with members and filter for user access
      const { data: projects, error } = await supabase
        .from('projects')
        .select(`
          *,
          project_members (
            user_id,
            role,
            status
          )
        `)
        .eq('is_research', false)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter projects where user is creator or active member using project_members table
      const userProjectIds = new Set<string>();

      // Always include projects created by the user
      (projects || []).forEach(project => {
        if (project.created_by === userId) {
          userProjectIds.add(project.id);
        }
      });

      // Get project memberships for this user
      const { data: memberships, error: membershipError } = await supabase
        .from('project_members')
        .select('project_id')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (!membershipError && memberships) {
        memberships.forEach(membership => {
          userProjectIds.add(membership.project_id);
        });
      }

      // Filter projects to only include those the user has access to
      allUserProjects = (projects || [])
        .filter(project => userProjectIds.has(project.id))
        .map(project => ({
          ...project,
          // Transform project_members into the expected members format for compatibility
          members: (project.project_members || [])
            .filter(member => member.status === 'active')
            .map(member => ({
              id: member.user_id,
              role: member.role,
              status: member.status
            }))
        }));

      // For clients, also check if they are associated with projects through the clients table (same as ProjectsContext)
      if (userProfile?.role === 'client' && userProfile?.email) {
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('current_projects')
          .eq('email', userProfile.email)
          .single();

        if (!clientError && clientData?.current_projects && clientData.current_projects.length > 0) {
          // Get projects that the client is associated with
          const { data: clientProjects, error: clientProjectsError } = await supabase
            .from('projects')
            .select('*')
            .eq('is_research', false)
            .in('id', clientData.current_projects)
            .order('created_at', { ascending: false });

          if (!clientProjectsError && clientProjects) {
            // Add client projects, avoiding duplicates
            const existingProjectIds = new Set(allUserProjects.map(p => p.id));
            const newClientProjects = clientProjects.filter(p => !existingProjectIds.has(p.id));
            allUserProjects = [...allUserProjects, ...newClientProjects];
          }
        }
      }

      // Count both active and archived projects (don't filter by status)
      const totalCount = allUserProjects.length;

      return totalCount;
    } catch (error) {
      console.error('Error getting actual project count:', error);
      return 0;
    }
  }

  // Contact sales for enterprise plan
  async contactSales(companyName: string, contactEmail: string, requirements: string): Promise<void> {
    try {
      // Enterprise inquiries continue through the website's contact page.
      window.location.assign('/contact');
    } catch (error) {
      console.error('Error contacting sales:', error);
      throw error;
    }
  }
}

export const paymentService = new PaymentService();
